import React, { useState, useEffect, useCallback } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebaseConfig.js';
import { useAuth } from '../../context/AuthContext';
import { hasAnyArrayData } from '../../utils/profileVisibility';
import SectionEditModal from './SectionEditModal';
import EditIconButton from './EditIconButton';
import CollaborationThumbnailCropModal from './CollaborationThumbnailCropModal';

/**
 * Uploads original and cropped collaboration thumbnails to Firebase Storage.
 * Paths: uploads/{userId}/collaborations/{collabId}/thumbnail-original.jpg, thumbnail-cropped.jpg
 * @param {File} originalFile - The original image file
 * @param {File} croppedFile - The cropped image file
 * @param {string} userId - The user's UID
 * @param {string} collabId - The collaboration ID
 * @returns {Promise<{ thumbnailOriginalUrl: string, thumbnailCroppedUrl: string }>}
 */
async function uploadCollaborationThumbnails(originalFile, croppedFile, userId, collabId) {
  if (!originalFile || !croppedFile || !userId || !collabId) {
    throw new Error('Missing file or user/collab ID');
  }
  const basePath = `uploads/${userId}/collaborations/${collabId}`;
  const originalRef = ref(storage, `${basePath}/thumbnail-original.jpg`);
  const croppedRef = ref(storage, `${basePath}/thumbnail-cropped.jpg`);
  await uploadBytes(originalRef, originalFile);
  await uploadBytes(croppedRef, croppedFile);
  const [thumbnailOriginalUrl, thumbnailCroppedUrl] = await Promise.all([
    getDownloadURL(originalRef),
    getDownloadURL(croppedRef),
  ]);
  return { thumbnailOriginalUrl, thumbnailCroppedUrl };
}

/**
 * Legacy: uploads a single collaboration thumbnail (no crop). Kept for backwards compatibility.
 */
async function uploadCollaborationThumbnail(file, userId, collabId) {
  if (!file || !userId || !collabId) throw new Error('No file or user/collab ID provided');
  const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `uploads/${userId}/collaborations/${collabId}.${extension}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

function CollaborationHighlightsSection({ collaborations, isOwner, onSaveCollaborations, mode = 'edit' }) {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formCollabs, setFormCollabs] = useState([]);
  const [newCollab, setNewCollab] = useState({
    brandName: '',
    timeframe: '',
    deliverables: '',
    keyResults: '',
    outcome: '',
    contentUrl: '',
  });
  const [thumbnailFiles, setThumbnailFiles] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const cropModalImageUrlRef = React.useRef(null);
  const [cropModal, setCropModal] = useState({
    open: false,
    collabId: null,
    imageSrc: null,
    originalFile: null,
  });

  useEffect(() => {
    if (isModalOpen) {
    const initial = (collaborations || []).map((c) => ({
      id: c.id || String(Date.now() + Math.random()),
      brandName: c.brandName || '',
      timeframe: c.timeframe || '',
      deliverables: c.deliverables || '',
      keyResults: c.keyResults || '',
      outcome: c.outcome || '',
      contentUrl: c.contentUrl || '',
      thumbnailUrl: c.thumbnailUrl || '',
      thumbnailOriginalUrl: c.thumbnailOriginalUrl || '',
      thumbnailCroppedUrl: c.thumbnailCroppedUrl || '',
      thumbnailCrop: c.thumbnailCrop || undefined,
      thumbnailAspect: c.thumbnailAspect ?? undefined,
    }));
    setFormCollabs(initial);
      setNewCollab({
        brandName: '',
        timeframe: '',
        deliverables: '',
        keyResults: '',
        outcome: '',
        contentUrl: '',
      });
      setThumbnailFiles({});
      setError(null);
      setCurrentPage(0);
    }
  }, [collaborations, isModalOpen]);

  const openCropModal = useCallback((collabId, file) => {
    if (!file) return;
    if (cropModalImageUrlRef.current) {
      URL.revokeObjectURL(cropModalImageUrlRef.current);
      cropModalImageUrlRef.current = null;
    }
    const imageSrc = URL.createObjectURL(file);
    cropModalImageUrlRef.current = imageSrc;
    setCropModal({ open: true, collabId, imageSrc, originalFile: file });
  }, []);

  const closeCropModal = useCallback(() => {
    if (cropModalImageUrlRef.current) {
      URL.revokeObjectURL(cropModalImageUrlRef.current);
      cropModalImageUrlRef.current = null;
    }
    setCropModal({ open: false, collabId: null, imageSrc: null, originalFile: null });
  }, []);

  const handleCropSave = useCallback(({ croppedFile, crop, aspect }) => {
    const { collabId, originalFile } = cropModal;
    if (!collabId || !originalFile) return;
    const previewUrl = URL.createObjectURL(croppedFile);
    setThumbnailFiles((prev) => ({
      ...prev,
      [collabId]: {
        originalFile,
        croppedFile,
        crop,
        aspect,
        previewUrl,
      },
    }));
    closeCropModal();
  }, [cropModal]);

  const handleRemoveThumbnail = (collabId) => {
    setFormCollabs((prev) =>
      prev.map((c) =>
        c.id === collabId
          ? {
              ...c,
              thumbnailUrl: '',
              thumbnailOriginalUrl: '',
              thumbnailCroppedUrl: '',
              thumbnailCrop: undefined,
              thumbnailAspect: undefined,
            }
          : c
      )
    );
    setThumbnailFiles((prev) => {
      const updated = { ...prev };
      const entry = updated[collabId];
      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      delete updated[collabId];
      return updated;
    });
  };

  const handleAddNewCollab = () => {
    const trimmedName = newCollab.brandName.trim();
    if (!trimmedName) {
      setError('Brand name is required to add a collaboration.');
      return;
    }

    const id = String(Date.now() + Math.random());

    if (thumbnailFiles['new']) {
      setThumbnailFiles((prev) => {
        const updated = { ...prev };
        updated[id] = updated['new'];
        delete updated['new'];
        return updated;
      });
    }

    setFormCollabs((prev) => [
      ...prev,
      {
        id,
        brandName: trimmedName,
        timeframe: newCollab.timeframe.trim(),
        deliverables: newCollab.deliverables.trim(),
        keyResults: newCollab.keyResults.trim(),
        outcome: newCollab.outcome.trim(),
        contentUrl: newCollab.contentUrl.trim(),
        thumbnailUrl: '',
        thumbnailOriginalUrl: '',
        thumbnailCroppedUrl: '',
        thumbnailCrop: undefined,
        thumbnailAspect: undefined,
      },
    ]);

    setNewCollab({
      brandName: '',
      timeframe: '',
      deliverables: '',
      keyResults: '',
      outcome: '',
      contentUrl: '',
    });
    setError(null);
  };

  const handleRemoveCollab = (id) => {
    setFormCollabs((prev) => prev.filter((c) => c.id !== id));
    setThumbnailFiles((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleSave = async () => {
    setError(null);

    if (!user?.uid) {
      setError('You must be logged in to save collaborations.');
      return;
    }

    try {
      setIsSaving(true);

      const uploadThumbnailEntry = async (collabId, entry) => {
        if (!entry) return {};
        if (entry.originalFile && entry.croppedFile) {
          const { thumbnailOriginalUrl, thumbnailCroppedUrl } = await uploadCollaborationThumbnails(
            entry.originalFile,
            entry.croppedFile,
            user.uid,
            collabId
          );
          return {
            thumbnailOriginalUrl,
            thumbnailCroppedUrl,
            thumbnailUrl: thumbnailCroppedUrl,
            thumbnailCrop: entry.crop,
            thumbnailAspect: entry.aspect,
          };
        }
        if (entry instanceof File || (entry && entry.name)) {
          const file = entry instanceof File ? entry : entry;
          const thumbnailUrl = await uploadCollaborationThumbnail(file, user.uid, collabId);
          return { thumbnailUrl };
        }
        return {};
      };

      const processedCollabs = await Promise.all(
        formCollabs.map(async (collab) => {
          const entry = thumbnailFiles[collab.id];
          let thumb = {};
          if (entry) {
            try {
              thumb = await uploadThumbnailEntry(collab.id, entry);
            } catch (err) {
              console.error('Error uploading thumbnail:', err);
              throw new Error(`Failed to upload thumbnail for ${collab.brandName}: ${err.message}`);
            }
          }
          return {
            id: collab.id,
            brandName: (collab.brandName || '').trim(),
            timeframe: (collab.timeframe || '').trim() || undefined,
            deliverables: (collab.deliverables || '').trim() || undefined,
            keyResults: (collab.keyResults || '').trim() || undefined,
            outcome: (collab.outcome || '').trim() || undefined,
            contentUrl: (collab.contentUrl || '').trim() || undefined,
            thumbnailUrl: thumb.thumbnailUrl ?? collab.thumbnailCroppedUrl ?? collab.thumbnailUrl,
            thumbnailOriginalUrl: thumb.thumbnailOriginalUrl ?? collab.thumbnailOriginalUrl,
            thumbnailCroppedUrl: thumb.thumbnailCroppedUrl ?? collab.thumbnailCroppedUrl,
            thumbnailCrop: thumb.thumbnailCrop ?? collab.thumbnailCrop,
            thumbnailAspect: thumb.thumbnailAspect ?? collab.thumbnailAspect,
          };
        })
      );

      if (newCollab.brandName.trim()) {
        const newId = String(Date.now() + Math.random());
        const entry = thumbnailFiles['new'];
        let thumb = {};
        if (entry) {
          try {
            thumb = await uploadThumbnailEntry(newId, entry);
          } catch (err) {
            console.error('Error uploading thumbnail for new collab:', err);
            throw new Error(`Failed to upload thumbnail: ${err.message}`);
          }
        }
        processedCollabs.push({
          id: newId,
          brandName: newCollab.brandName.trim(),
          timeframe: newCollab.timeframe.trim() || undefined,
          deliverables: newCollab.deliverables.trim() || undefined,
          keyResults: newCollab.keyResults.trim() || undefined,
          outcome: newCollab.outcome.trim() || undefined,
          contentUrl: newCollab.contentUrl.trim() || undefined,
          thumbnailUrl: thumb.thumbnailUrl,
          thumbnailOriginalUrl: thumb.thumbnailOriginalUrl,
          thumbnailCroppedUrl: thumb.thumbnailCroppedUrl,
          thumbnailCrop: thumb.thumbnailCrop,
          thumbnailAspect: thumb.thumbnailAspect,
        });
      }

      // Filter out collabs without brandName
      const cleaned = processedCollabs.filter((c) => c.brandName.length > 0);

      await onSaveCollaborations(cleaned);
      setIsModalOpen(false);
      setThumbnailFiles({});
      setNewCollab({
        brandName: '',
        timeframe: '',
        deliverables: '',
        keyResults: '',
        outcome: '',
        contentUrl: '',
      });
    } catch (e) {
      console.error('Error saving collaborations:', e);
      setError(e.message || 'Failed to save collaborations. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setError(null);
    setCurrentPage(0);
    // Reset form from source
    const reset = (collaborations || []).map((c) => ({
      id: c.id || String(Date.now() + Math.random()),
      brandName: c.brandName || '',
      timeframe: c.timeframe || '',
      deliverables: c.deliverables || '',
      keyResults: c.keyResults || '',
      outcome: c.outcome || '',
      contentUrl: c.contentUrl || '',
      thumbnailUrl: c.thumbnailUrl || '',
      thumbnailOriginalUrl: c.thumbnailOriginalUrl || '',
      thumbnailCroppedUrl: c.thumbnailCroppedUrl || '',
      thumbnailCrop: c.thumbnailCrop,
      thumbnailAspect: c.thumbnailAspect,
    }));
    setFormCollabs(reset);
    setNewCollab({
      brandName: '',
      timeframe: '',
      deliverables: '',
      keyResults: '',
      outcome: '',
      contentUrl: '',
    });
    setThumbnailFiles({});
  };

  // VIEW MODE
  const renderViewMode = () => {
    const hasCollaborations = hasAnyArrayData(collaborations);
    const itemsPerPage = 3;
    const totalPages = Math.ceil((collaborations?.length || 0) / itemsPerPage);
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentCollaborations = hasCollaborations ? collaborations.slice(startIndex, endIndex) : [];

    // Hide section if empty in public mode
    if (mode === 'public' && !hasCollaborations) {
      return null;
    }

    return (
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 md:p-6 mb-5 md:mb-6 relative">
        <div className="mb-3 md:mb-4 flex items-center justify-between gap-2">
          <h2 className="text-lg md:text-xl font-semibold text-slate-900">
            Collaboration Highlights
          </h2>
        </div>
        {isOwner && (
          <div className="absolute right-4 sm:right-6 top-4 sm:top-6 z-10">
            <EditIconButton onClick={() => setIsModalOpen(true)} />
          </div>
        )}

        {hasCollaborations ? (
          <div>
            {/* Grid of 3 collaborations */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              {currentCollaborations.map((collab) => {
                const initial = (collab.brandName || '').trim().charAt(0).toUpperCase();

                return (
                  <div
                    key={collab.id || collab.brandName}
                    className="rounded-lg border border-slate-200 bg-white p-3 space-y-2"
                  >
                    {/* Smaller Thumbnail */}
                    <div className="aspect-video rounded-md border border-slate-200 overflow-hidden bg-slate-100 flex items-center justify-center">
                      {(collab.thumbnailCroppedUrl || collab.thumbnailUrl) ? (
                        <img
                          src={collab.thumbnailCroppedUrl || collab.thumbnailUrl}
                          alt={collab.brandName || 'Collaboration thumbnail'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const parent = e.target.parentElement;
                            if (parent && !parent.querySelector('.fallback-initial')) {
                              const fallback = document.createElement('div');
                              fallback.className = 'fallback-initial w-full h-full flex items-center justify-center text-lg font-bold text-slate-600';
                              fallback.textContent = initial;
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-lg font-bold text-slate-600">
                          {initial}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="space-y-1">
                      <div className="text-xs font-semibold text-slate-900 truncate">
                        {collab.brandName || 'Unnamed Brand'}
                      </div>

                      {collab.timeframe && (
                        <div className="text-xs text-slate-600 truncate">
                          {collab.timeframe}
                        </div>
                      )}

                      {collab.deliverables && (
                        <div className="text-xs text-slate-600 truncate">
                          {collab.deliverables}
                        </div>
                      )}

                      {collab.keyResults && (
                        <div className="text-xs text-slate-600 truncate">
                          {collab.keyResults}
                        </div>
                      )}

                      {collab.contentUrl && (
                        <div>
                          <a
                            href={collab.contentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-slate-700 underline hover:text-slate-900"
                          >
                            View →
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination arrows */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                  className="flex items-center justify-center w-8 h-8 rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Previous page"
                >
                  ←
                </button>
                <span className="text-xs text-slate-600">
                  {currentPage + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="flex items-center justify-center w-8 h-8 rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Next page"
                >
                  →
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            No collaboration highlights added yet.
            {isOwner &&
              " Add case studies of your best brand partnerships to showcase your work."}
          </p>
        )}
      </section>
    );
  };

  // EDIT MODE - Render form content for modal
  const renderEditForm = () => {
  return (
      <div className="space-y-4">
        {/* Existing collaborations */}
        <div className="space-y-4">
          {formCollabs.length > 0 ? (
            formCollabs.map((collab, idx) => {
              const thumbEntry = thumbnailFiles[collab.id];
              const hasThumbnail =
                collab.thumbnailUrl ||
                collab.thumbnailCroppedUrl ||
                (thumbEntry && (thumbEntry.previewUrl || thumbEntry.croppedFile));
              const thumbnailPreview =
                thumbEntry?.previewUrl || collab.thumbnailCroppedUrl || collab.thumbnailUrl;

              return (
                <div
                  key={collab.id}
                  className="border border-slate-200 rounded-md p-4 space-y-3"
                >
                  {/* Thumbnail preview/upload */}
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-2">
                      Thumbnail (optional)
                    </label>
                    {hasThumbnail ? (
                      <div className="relative">
                        <div className="aspect-video rounded-md border border-slate-200 overflow-hidden max-w-xs">
                          <img
                            src={thumbnailPreview}
                            alt="Thumbnail preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                        <div className="mt-2 flex gap-2">
                          <label className="cursor-pointer inline-flex items-center rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200">
                            Change
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) openCropModal(collab.id, file);
                                e.target.value = '';
                              }}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => handleRemoveThumbnail(collab.id)}
                            className="inline-flex items-center rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="cursor-pointer inline-flex items-center rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200">
                        Upload thumbnail
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) openCropModal(collab.id, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                  </div>

                  {/* Form fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600">
                        Brand name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                        value={collab.brandName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormCollabs((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], brandName: value };
                            return copy;
                          });
                        }}
                        required
                      />
                    </div>

                    <div>
                      <label className="text-xs font-medium text-slate-600">Timeframe</label>
                      <input
                        type="text"
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                        value={collab.timeframe}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormCollabs((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], timeframe: value };
                            return copy;
                          });
                        }}
                        placeholder="e.g., June 2024"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-medium text-slate-600">Deliverables</label>
                      <input
                        type="text"
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                        value={collab.deliverables}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormCollabs((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], deliverables: value };
                            return copy;
                          });
                        }}
                        placeholder="e.g., 1 TikTok + 1 Reel"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-medium text-slate-600">Key Results</label>
                      <input
                        type="text"
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                        value={collab.keyResults}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormCollabs((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], keyResults: value };
                            return copy;
                          });
                        }}
                        placeholder="e.g., 182k views, 12.2% engagement"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-xs font-medium text-slate-600">Outcome</label>
                      <textarea
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                        value={collab.outcome}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormCollabs((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], outcome: value };
                            return copy;
                          });
                        }}
                        rows={2}
                        placeholder="Short 1-2 sentence summary"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-xs font-medium text-slate-600">Content URL</label>
                      <input
                        type="url"
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                        value={collab.contentUrl}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormCollabs((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], contentUrl: value };
                            return copy;
                          });
                        }}
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={() => handleRemoveCollab(collab.id)}
                      className="text-xs font-medium text-red-500 hover:text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-slate-500">
              You haven't added any collaborations yet. Start by adding one below.
            </p>
          )}
        </div>

        {/* Add new collaboration */}
        <div className="border border-slate-200 rounded-md p-4 space-y-3 bg-slate-50/60">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Add new collaboration
          </div>

          {/* Thumbnail for new collab */}
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-2">
              Thumbnail (optional)
            </label>
            {thumbnailFiles['new'] ? (
              <div className="relative">
                <div className="aspect-video rounded-md border border-slate-200 overflow-hidden max-w-xs">
                  <img
                    src={thumbnailFiles['new'].previewUrl}
                    alt="Thumbnail preview"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-2 flex gap-2">
                  <label className="cursor-pointer inline-flex items-center rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200">
                    Change
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) openCropModal('new', file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const entry = thumbnailFiles['new'];
                      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
                      setThumbnailFiles((prev) => {
                        const updated = { ...prev };
                        delete updated['new'];
                        return updated;
                      });
                    }}
                    className="inline-flex items-center rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <label className="cursor-pointer inline-flex items-center rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200">
                Upload thumbnail
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) openCropModal('new', file);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">
                Brand name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={newCollab.brandName}
                onChange={(e) =>
                  setNewCollab((prev) => ({ ...prev, brandName: e.target.value }))
                }
                placeholder="e.g., Nike, Glossier"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Timeframe</label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={newCollab.timeframe}
                onChange={(e) =>
                  setNewCollab((prev) => ({ ...prev, timeframe: e.target.value }))
                }
                placeholder="e.g., June 2024"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Deliverables</label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={newCollab.deliverables}
                onChange={(e) =>
                  setNewCollab((prev) => ({ ...prev, deliverables: e.target.value }))
                }
                placeholder="e.g., 1 TikTok + 1 Reel"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Key Results</label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={newCollab.keyResults}
                onChange={(e) =>
                  setNewCollab((prev) => ({ ...prev, keyResults: e.target.value }))
                }
                placeholder="e.g., 182k views, 12.2% engagement"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-600">Outcome</label>
              <textarea
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={newCollab.outcome}
                onChange={(e) =>
                  setNewCollab((prev) => ({ ...prev, outcome: e.target.value }))
                }
                rows={2}
                placeholder="Short 1-2 sentence summary"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-600">Content URL</label>
              <input
                type="url"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                value={newCollab.contentUrl}
                onChange={(e) =>
                  setNewCollab((prev) => ({ ...prev, contentUrl: e.target.value }))
                }
                placeholder="https://..."
              />
            </div>
          </div>

          <div>
            <button
              type="button"
              onClick={handleAddNewCollab}
              className="mt-2 inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
            >
              Add collaboration
            </button>
          </div>
        </div>

      </div>
    );
  };

  return (
    <>
      {renderViewMode()}
      
      {isOwner && (
        <>
          <SectionEditModal
            isOpen={isModalOpen}
            onClose={handleCancel}
            title="Edit Collaboration Highlights"
            subtitle="Showcase your best partnerships, deliverables, and results."
            onSave={handleSave}
            isSaving={isSaving}
            errors={error ? { _general: error } : {}}
          >
            {renderEditForm()}
          </SectionEditModal>
          <CollaborationThumbnailCropModal
            isOpen={cropModal.open}
            imageSrc={cropModal.imageSrc}
            onCancel={closeCropModal}
            onSave={handleCropSave}
          />
        </>
      )}
    </>
  );
}

export default CollaborationHighlightsSection;

